// Copyright 2021 Rennie Glen Software LLC

/**
 * Escapes regular expression special characters in a string.
 */
export function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

/**
 * Replaces keys with values in a text string.
 * 
 * @param text The text to find and replace strings in.
 * @param replacements The keys and values to replace in text.
 * @returns Text with replacements.
 */
export function multiReplace(text: string, replacements: Map<string, string>): string {
  const keys: string[] = [];
  for (const key of replacements.keys()) {
    keys.push(escapeRegExp(key));
  }
  const regExp = new RegExp(keys.join('|'), 'g');
  return text.replace(regExp, key => replacements.get(key) ?? '');
}

/**
 * Joins two strings with a single forward slash between them.  Correctly handles
 * cases where a ends with a slash or b begins with a slash.
 */
export function joinWithOneSlash(a: string, b: string): string {
  if (a.endsWith('/')) {
    return b.startsWith('/')
      ? a.slice(0, a.length - 1) + b
      : a + b;
  } else {
    return b.startsWith('/')
      ? a + b
      : `${a}/${b}`
  }
}


/** Returns a 3 digit summary of the bytes like "23.4 MB" */
export function formatBytes(bytes: number) {
  if (bytes === 0) return '0 Bytes';
  let sign = '';
  if (bytes < 0) {
    sign = '-';
    bytes = -bytes;
  }

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return sign + Number((bytes / Math.pow(k, i)).toPrecision(2)) + ' ' + sizes[i];
}

export function last6(s: string): string {
  const len = s.length;
  if (len <= 6) {
    return s;
  }
  return s.substring(len - 6)
}

/// Returns the current time in the format required by JDF.
export function getJDFTimestamp(): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
  
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1); // Months are 0-based
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());
  
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}
